import React, { useEffect, useMemo, useState } from "react";

import {
  closestCenter,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import Grid from "@material-ui/core/Grid";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { deleteAttachment, setSetting } from "actions";

import { updateAttachments, uploadRequest } from "actions/files";

import { SubtleBadge } from "components/Badge";
import { Thumbnail } from "components/ClearingSaleCards/VendorCard";
import { ConfirmDialog, createModalTitle } from "components/ConfirmDialog";
import { CollapseLabel, FormCollapse } from "components/Form";
import {
  FileInput,
  Input,
  withNamespace,
} from "components/Form/FormikControls";
import { CenteredGreedy, Row } from "components/Layout";
import { LightBox } from "components/LightBox";
import LoadingSpinner from "components/LoadingSpinner";
import { DeletableThumbnail } from "components/Media";
import { OptionToggler } from "components/OptionToggler";
import { SortableItem } from "components/SortableItem";
import { BigText, MediumText, Text } from "components/Text";

import { ClearingSaleLotStatus } from "constants/clearingSaleAttributes";
import { DocumentType, NVDRelatedDocumentTypes } from "constants/documentTypes";
import {
  ACCEPTED_FILE_TYPES,
  NVD_ALLOWED_TYPES,
  VIDEO_FILE_EXTENSIONS,
} from "constants/files";
import { SaleType } from "constants/sale";
import { SaleLotNamespace } from "constants/saleLots";
import { Settings } from "constants/settings";

import { EMPTY_ARRAY, formatPercentage } from "lib";

import { filterBySaleType } from "lib/attachments";

import {
  getHasWriteAccessInCurrentSale,
  getIsConsigningAgent,
  getIsLivestockAgent,
  getIsSaleLocked,
  getSaleLotById,
  getSettings,
  selectAttachmentsByConsignmentId,
  selectAttachmentsBySaleLotId,
} from "selectors";

import { useHasBillingPermission } from "hooks";

const Gallery = styled.div`
  ${({ theme }) => `
  padding: 0 ${theme.space[2]}px;
  display: grid;
  flex: 1;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: ${theme.space[2]}px;
  @media only screen and (max-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
    grid-gap: ${theme.space[1]}px;
  }
  
  transition: all 0.2s ease-in-out;
  > * {
    max-width: 100%;
    box-shadow:  ${theme.shadows[1]};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    &:hover {
      transform: scale(1.1);
    }
  }
`}
`;

const consignmentAttachmentBillingEmailText = {
  [DocumentType.NVD]: "Files Uploaded to NVD can be sent with Invoices",
  [DocumentType.INTERNAL_DOCUMENTATION]:
    "Files uploaded to the Internal Documentation are not sent to anyone.",
  [DocumentType.SUPPORTING_DOCUMENTATION]:
    "Files Uploaded to Supporting Documentation can be sent with Invoices",
  [DocumentType.ANIMAL_HEALTH_STATEMENT]:
    "Files Uploaded to Animal Health Statement can be sent with Invoices",
  [DocumentType.OTHER]: "Files Uploaded to Other can be sent with Invoices",
  [DocumentType.KILL_SHEET]:
    "Files uploaded to Kill Sheet can be sent with RCTIs",
  [DocumentType.INSURANCE_CLAIM]:
    "Files uploaded to Insurance Claim can be sent with RCTIs",
};

export const MediaCollapse = ({
  isOpen,
  onToggle,
  saleLotId = null,
  consignmentId = null,
  readOnly,
  saleType,
  namespace = "",
}) => {
  const attachments =
    useSelector(state =>
      saleLotId
        ? selectAttachmentsBySaleLotId(state)[saleLotId]
        : selectAttachmentsByConsignmentId(state)[consignmentId],
    ) || [];

  const youtube_link =
    useSelector(getSaleLotById(saleLotId))?.youtube_link || "";

  const documentAttachmentsCount = attachments.filter(attachment =>
    NVDRelatedDocumentTypes.includes(attachment.type),
  )?.length;

  const imageAttachmentsCount = attachments.filter(
    attachment => attachment.type === DocumentType.IMAGE,
  )?.length;

  const videoAttachmentsCount = attachments.filter(
    attachment => attachment.type === DocumentType.VIDEO,
  )?.length;

  const internalDocumentationCount = attachments.filter(
    attachment => attachment.type === DocumentType.INTERNAL_DOCUMENTATION,
  )?.length;

  const imageCountSummary = `${imageAttachmentsCount || 0} Image${
    imageAttachmentsCount === 1 ? "" : "s"
  }`;

  const videoCountSummary = `${videoAttachmentsCount} Video${
    videoAttachmentsCount === 1 ? "" : "s"
  }`;

  const documentCountSummary = `${documentAttachmentsCount} Document${
    documentAttachmentsCount === 1 ? "" : "s"
  }`;

  const internalDocumentationSummary = `${internalDocumentationCount} Internal Document${
    internalDocumentationCount === 1 ? "" : "s"
  }`;

  const enableInternalDocumentation = !saleLotId;
  const enableYoutubeLink = !!saleLotId;

  const videoSummary = `${youtube_link ? "" : "No "}External Video Link`;

  const mediaCollapseInfo = [
    imageCountSummary,
    videoCountSummary,
    documentCountSummary,
  ];

  if (enableInternalDocumentation) {
    mediaCollapseInfo.push(internalDocumentationSummary);
  }

  if (enableYoutubeLink) {
    mediaCollapseInfo.push(videoSummary);
  }

  const header = (
    <Row>
      <SubtleBadge>Media</SubtleBadge>
      <Row alignCenter flexWrap>
        <CollapseLabel>{mediaCollapseInfo.join(", ")}</CollapseLabel>
      </Row>
    </Row>
  );

  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={onToggle}
      header={header}
      id="collapse-media"
      dataTour={isOpen ? "hideMedia" : "showMedia"}
    >
      {isOpen && (
        <MediaCollapseBody
          saleLotId={saleLotId}
          consignmentId={consignmentId}
          readOnly={readOnly}
          saleType={saleType}
          namespace={namespace}
        />
      )}
    </FormCollapse>
  );
};

const SaleLotAttachmentTypeOptions = [
  {
    label: "Image/Media",
    value: DocumentType.IMAGE,
  },
  {
    label: "Video",
    value: DocumentType.VIDEO,
  },
  {
    label: DocumentType.SUPPORTING_DOCUMENTATION,
    value: DocumentType.SUPPORTING_DOCUMENTATION,
  },
];

const ConsignmentAttachmentTypeOptions = [
  {
    label: DocumentType.NVD,
    value: DocumentType.NVD,
  },
  {
    label: DocumentType.ANIMAL_HEALTH_STATEMENT,
    value: DocumentType.ANIMAL_HEALTH_STATEMENT,
  },
  {
    label: "Image/Media",
    value: DocumentType.IMAGE,
  },
  {
    label: "Video",
    value: DocumentType.VIDEO,
  },
  {
    label: DocumentType.SUPPORTING_DOCUMENTATION,
    value: DocumentType.SUPPORTING_DOCUMENTATION,
  },
  {
    label: DocumentType.OTHER,
    value: DocumentType.OTHER,
  },
  {
    label: DocumentType.KILL_SHEET,
    value: DocumentType.KILL_SHEET,
    saleTypes: [SaleType.OVER_HOOKS],
  },
  {
    label: DocumentType.INSURANCE_CLAIM,
    value: DocumentType.INSURANCE_CLAIM,
  },
  {
    label: DocumentType.INTERNAL_DOCUMENTATION,
    value: DocumentType.INTERNAL_DOCUMENTATION,
  },
];

export const MediaCollapseBody = React.memo(
  ({ saleLotId, consignmentId, readOnly, saleType, namespace }) => {
    const [preview, setPreview] = React.useState(null);
    const [activeFile, setActiveFile] = useState(null);
    const dispatch = useDispatch();

    const hasBillingPermissions = useHasBillingPermission();

    const attachmentTypeOptions = useMemo(() => {
      const options = saleLotId
        ? SaleLotAttachmentTypeOptions
        : ConsignmentAttachmentTypeOptions;

      return options.filter(filterBySaleType(saleType));
    }, [saleLotId, saleType]);

    const attachmentTypeSetting = saleLotId
      ? Settings.consignmentAttachmentTypeSelection
      : Settings.saleLotAttachmentTypeSelection;

    const attachmentType =
      useSelector(getSettings)[attachmentTypeSetting] || DocumentType.IMAGE;

    const resolveAttachmentType = file => {
      // if a file contains a video extension, it should register as a video
      // regardless of which tab it is uplaoded to
      if (
        VIDEO_FILE_EXTENSIONS.some(extension => file.name.includes(extension))
      ) {
        return DocumentType.VIDEO;
      }
      return attachmentType;
    };

    const setAttachmentType = value => {
      dispatch(setSetting(attachmentTypeSetting, value));
    };

    const attachments =
      useSelector(state =>
        saleLotId
          ? selectAttachmentsBySaleLotId(state)[saleLotId]
          : selectAttachmentsByConsignmentId(state)[consignmentId],
      ) || EMPTY_ARRAY;

    const filteredAttachments = useMemo(
      () =>
        attachments.filter(attachment => attachment.type === attachmentType),
      [attachmentType, attachments],
    );

    const [sortableAttachments, setSortableAttachments] =
      useState(filteredAttachments);

    useEffect(() => {
      setSortableAttachments(filteredAttachments);
    }, [filteredAttachments]);

    const hasWriteAccessInCurrentSale = useSelector(
      getHasWriteAccessInCurrentSale,
    );
    const isConsigningAgent = useSelector(getIsConsigningAgent);
    // If this is used on the consignment modal, the value here should be null.
    const saleLot = useSelector(getSaleLotById(saleLotId));

    const isSaleLocked = useSelector(getIsSaleLocked);

    const isLivestockAgent = useSelector(getIsLivestockAgent);

    const isLivestockAgentInLockedSale = isLivestockAgent && isSaleLocked;

    // Allow addition of new files if we have write access, or it's a consigning agent editing a Pending salelot.
    const allowUpload =
      hasWriteAccessInCurrentSale ||
      isLivestockAgentInLockedSale ||
      (isConsigningAgent &&
        saleLot &&
        saleLot[SaleLotNamespace.CLEARING_SALE_ATTRIBUTES]?.status ===
          ClearingSaleLotStatus.PENDING);

    const [deleteDialogFile, setDeleteFileDialog] = React.useState(null);
    const deleteFile = () => {
      dispatch(deleteAttachment(deleteDialogFile));
      setDeleteFileDialog(null);
    };

    const enableYoutubeLink = !!saleLotId;

    const sensors = useSensors(
      useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
      useSensor(TouchSensor, { activationConstraint: { distance: 0 } }),
    );

    function handleDragEnd(event) {
      const { active, over } = event;
      if (active.id !== over.id) {
        setSortableAttachments(sortableAttachments => {
          const oldIndex = sortableAttachments.findIndex(
            attachment => attachment.id === active.id,
          );
          const newIndex = sortableAttachments.findIndex(
            attachment => attachment.id === over.id,
          );
          const newItems = arrayMove(sortableAttachments, oldIndex, newIndex);
          dispatch(
            updateAttachments(
              newItems.map((attachment, index) => ({
                id: attachment.id,
                order: index,
              })),
            ),
          );
          setActiveFile(null);
          return newItems;
        });
      }
    }

    function handleDragCancel() {
      setActiveFile(null);
    }

    function handleDragStart(event) {
      const file = sortableAttachments.find(
        attachment => attachment.id === event.active.id,
      );
      setActiveFile(file);
    }

    const upload = file => {
      const formData = {
        sale_lot: saleLotId,
        consignment: consignmentId,
        type: resolveAttachmentType(file),
        order: attachments.length,
      };
      dispatch(uploadRequest(file, formData));
    };

    const closePreview = () => setPreview(null);

    let uploadBlock = null;

    // if we are a livestock agent in a locked sale we allow uploads to any document type
    // beside NVDs and Animal Health Statements
    if (
      isLivestockAgentInLockedSale &&
      (attachmentType === DocumentType.NVD ||
        attachmentType === DocumentType.ANIMAL_HEALTH_STATEMENT)
    ) {
      uploadBlock = null;
    } else if (
      attachmentType === DocumentType.NVD &&
      filteredAttachments.length > 0
    ) {
      uploadBlock = (
        <CenteredGreedy>
          <BigText>Only one NVD can be uploaded per consignment.</BigText>
          <MediumText>
            Secondary NVDs can be uploaded as &quot;Other&quot;.
          </MediumText>
        </CenteredGreedy>
      );
    } else if (allowUpload && (isLivestockAgentInLockedSale || !readOnly)) {
      uploadBlock = (
        <FileInput
          upload={upload}
          rotate={NVDRelatedDocumentTypes.includes(attachmentType)}
          allowedFileTypes={
            attachmentType === DocumentType.NVD
              ? NVD_ALLOWED_TYPES
              : ACCEPTED_FILE_TYPES
          }
        />
      );
    }

    const canEditImageInLockedSale =
      isLivestockAgentInLockedSale &&
      !filteredAttachments.some(attachment =>
        [DocumentType.ANIMAL_HEALTH_STATEMENT, DocumentType.NVD].includes(
          attachment.type,
        ),
      );

    const getAttachementNameFromURL = url => {
      const strings = url.split("/");
      const attachmentName = strings[strings.length - 1];
      return attachmentName;
    };

    return (
      <>
        <>
          <Grid item xs={12}>
            {/* TODO: Handle attachments that dont have a type yet. */}
            <OptionToggler
              name="Attachment Type"
              options={attachmentTypeOptions}
              value={attachmentType}
              onChange={setAttachmentType}
              width="100%"
            />
          </Grid>
          {/* Show billing email details if in a consignment modal and has billing permissions */}
          {hasBillingPermissions && !saleLotId && (
            <Grid item xs={12}>
              <Text>
                {consignmentAttachmentBillingEmailText[attachmentType]}
              </Text>
            </Grid>
          )}
          <Grid item xs={12}>
            {uploadBlock}
          </Grid>
          <Grid item xs={12}>
            <Gallery>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
                onDragCancel={handleDragCancel}
              >
                <SortableContext
                  items={sortableAttachments}
                  strategy={rectSortingStrategy}
                >
                  {sortableAttachments?.map((attachment, index) =>
                    attachment.isFinished === false ? (
                      <CenteredGreedy key={attachment.id}>
                        <LoadingSpinner
                          actionName="Uploading"
                          subjectName={formatPercentage(
                            attachment.progress || "",
                          )}
                        />
                      </CenteredGreedy>
                    ) : (
                      <SortableItem
                        key={attachment.id}
                        id={attachment.id}
                        data-tour={getAttachementNameFromURL(
                          attachment.image_url,
                        )}
                      >
                        <DeletableThumbnail
                          key={attachment.id}
                          handleDelete={() => {
                            setDeleteFileDialog(attachment);
                          }}
                          file={attachment}
                          index={index}
                          setPreview={setPreview}
                          readOnly={readOnly}
                        />
                      </SortableItem>
                    ),
                  )}
                </SortableContext>
                <DragOverlay>
                  {activeFile ? <Thumbnail file={activeFile} /> : null}
                </DragOverlay>
              </DndContext>
            </Gallery>
          </Grid>
          {enableYoutubeLink && (
            <Grid item xs={12}>
              <Input
                label="Youtube Link"
                name={withNamespace(namespace, "youtube_link")}
                disabled={readOnly}
              />
            </Grid>
          )}
        </>

        {attachments?.length > 0 && preview && (
          <LightBox
            defaultId={preview}
            onClose={closePreview}
            images={attachments}
            attachmentTypeOptions={attachmentTypeOptions}
            readOnly={!canEditImageInLockedSale && readOnly}
          />
        )}
        {deleteDialogFile && (
          <ConfirmDialog
            title={createModalTitle("this Attachment")}
            isOpen={deleteDialogFile}
            onCancel={() => setDeleteFileDialog(null)}
            onDelete={deleteFile}
          />
        )}
      </>
    );
  },
);
